import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Avatar, Chip, CircularProgress,
    Stack, Paper, Button, IconButton, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    InputAdornment
} from '@mui/material';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import EditIcon          from '@mui/icons-material/Edit';
import Visibility        from '@mui/icons-material/Visibility';
import VisibilityOff     from '@mui/icons-material/VisibilityOff';
import LocationCityIcon  from '@mui/icons-material/LocationCity';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PauseCircleIcon   from '@mui/icons-material/PauseCircle';
import PlayCircleIcon    from '@mui/icons-material/PlayCircle';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation }         from 'react-i18next';
import axios                      from '../api/axiosConfig';
import { useAuth }                from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

function InfoRow({ icon, label, value }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{
            py: 1.5, borderBottom: '1px solid rgba(196,98,45,0.08)',
            '&:last-child': { border: 'none' },
        }}>
            <Box sx={{ color: TERRACOTTA, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: value ? WALNUT : 'text.disabled' }}>
                    {value || '—'}
                </Typography>
            </Box>
        </Stack>
    );
}

export default function ManagerProfilePage() {
    const { id }   = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t }    = useTranslation();

    const isAdmin = user?.role === 'ADMIN';

    const [manager,    setManager]    = useState(null);
    const [districts,  setDistricts]  = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [notAllowed, setNotAllowed] = useState(false);
    const [editOpen,   setEditOpen]   = useState(false);

    const [form,      setForm]      = useState({ districtId: '', startDate: '' });
    const [saving,    setSaving]    = useState(false);
    const [formError, setFormError] = useState('');

    const [pwdOpen,    setPwdOpen]    = useState(false);
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);

    const [fetchError, setFetchError] = useState(false);

    // Status toggle (inactivate/reactivate)
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError,   setStatusError]   = useState('');

    useEffect(() => {
        if (user && !isAdmin) navigate('/', { replace: true });
    }, [user, isAdmin, navigate]);

    useEffect(() => {
        if (!id) return;
        Promise.all([
            axios.get(`/managers/view/id/${id}`),
            axios.get('/districts/view?size=1000'),
        ])
            .then(([mRes, dRes]) => {
                setManager(mRes.data);
                setDistricts(Array.isArray(dRes.data) ? dRes.data : (dRes.data?.content || []));
            })
            .catch(err => {
                if (err.response?.status === 403) setNotAllowed(true);
                else setFetchError(true);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const toggleManagerStatus = async () => {
        const newStatus = manager.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        setStatusLoading(true);
        setStatusError('');
        try {
            const res = await axios.patch(`/managers/${id}/status?status=${newStatus}`);
            setManager(prev => ({ ...prev, status: res.data.status }));
        } catch {
            setStatusError(t('guide_profile.status_failed', 'Failed to update status.'));
        } finally {
            setStatusLoading(false);
        }
    };

    /* ── FIX: Pre-fill form from current manager data when opening the dialog,
       not during initial data load. Same pattern as GuideProfilePage fix. ──── */
    const openEdit = () => {
        setForm({
            districtId: manager?.districtId || '',
            startDate:  manager?.startDate  || '',
        });
        setFormError('');
        setEditOpen(true);
    };
    const closeEdit = () => setEditOpen(false);

    /* ── FIX: Detect actual changes by comparing against current manager state ─ */
    const submitEdit = (e) => {
        e.preventDefault();
        const districtChanged = form.districtId && form.districtId !== (manager?.districtId || '');
        const dateChanged     = form.startDate  && form.startDate  !== (manager?.startDate  || '');

        if (!districtChanged && !dateChanged) {
            setFormError(t('guide_profile.nothing_changed', 'No changes to save.'));
            return;
        }
        setFormError('');
        setEditOpen(false);
        setPwdOpen(true);
    };

    const closePwd = () => { setPwdOpen(false); setPassword(''); setPwdError(''); setShowPwd(false); };

    const confirmSave = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setPwdLoading(true);
        setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: user.email, password });

            const payload = {};
            if (form.districtId) payload.districtId = form.districtId;
            if (form.startDate)  payload.startDate  = form.startDate;

            const res = await axios.patch(`/managers/${id}/assignment`, payload);

            /* ── FIX: Merge response but preserve user-display fields.
               The ManagerDto PATCH response may have null username/email/imageUrl
               if the backend's Hibernate session doesn't eagerly load the User
               association after save(). We keep existing values for any field the
               response returns as null/undefined. ──────────────────────────────── */
            setManager(prev => {
                const updated = { ...prev };
                const data = res.data;
                // Always take assignment fields from response
                if (data.districtId   !== undefined) updated.districtId   = data.districtId;
                if (data.districtName !== undefined) updated.districtName = data.districtName;
                if (data.startDate    !== undefined) updated.startDate    = data.startDate;
                // Only overwrite user fields if the response actually has them
                if (data.username) updated.username = data.username;
                if (data.email)    updated.email    = data.email;
                if (data.imageUrl !== undefined) updated.imageUrl = data.imageUrl;
                if (data.userId)   updated.userId   = data.userId;
                return updated;
            });
            closePwd();
        } catch (err) {
            const code = err.response?.data?.error;
            const st   = err.response?.status;
            setPwdError(
                st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                    ? t('users.wrong_password')
                    : t('guide_profile.save_failed', 'Failed to save.')
            );
        } finally {
            setPwdLoading(false);
        }
    };

    if (!user || !isAdmin) return null;

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: TERRACOTTA }} />
        </Box>
    );

    if (notAllowed) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: WALNUT }}>{t('guide_profile.not_allowed', 'Access denied.')}</Typography>
            <Button onClick={() => navigate(-1)} sx={{ color: TERRACOTTA }}>
                {t('common.back', 'Back')}
            </Button>
        </Box>
    );

    if (!manager) return null;

    /* ── FIX: District label — try districtName, then lookup by id with name → region fallback ── */
    const districtLabel = manager.districtName
        || districts.find(d => d.id === manager.districtId)?.name
        || districts.find(d => d.id === manager.districtId)?.region
        || '—';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${FOREST} 100%)`,
                height: 130, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="md" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="small"
                                sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                        {t('manager_profile.overline', 'Manager Profile')}
                    </Typography>
                </Stack>

                {/* Profile header */}
                <Paper elevation={0} sx={{
                    p: 3, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 3,
                    border: '1px solid rgba(196,98,45,0.1)', bgcolor: 'white',
                    boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                }}>
                    <Avatar src={manager.imageUrl} alt={manager.username}
                            sx={{ width: 72, height: 72, bgcolor: GOLD,
                                border: '3px solid rgba(201,151,58,0.2)', flexShrink: 0 }}>
                        {manager.username?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={800}
                                    sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {manager.username}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{manager.email}</Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                            <Chip label="MANAGER" size="small"
                                  sx={{ bgcolor: GOLD, color: WALNUT, fontWeight: 700, fontSize: '0.7rem' }} />
                            {manager.status === 'INACTIVE' && (
                                <Chip label={t('guide_profile.inactive', 'INACTIVE')} size="small"
                                      sx={{ bgcolor: '#8B2020', color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />
                            )}
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        {manager.status === 'INACTIVE' ? (
                            <Button variant="outlined" startIcon={<PlayCircleIcon />}
                                    onClick={toggleManagerStatus} disabled={statusLoading}
                                    sx={{ borderColor: FOREST, color: FOREST, borderRadius: 2, fontWeight: 700,
                                        '&:hover': { bgcolor: 'rgba(74,103,65,0.06)' } }}>
                                {t('guide_profile.reactivate', 'Reactivate')}
                            </Button>
                        ) : (
                            <Button variant="outlined" startIcon={<PauseCircleIcon />}
                                    onClick={toggleManagerStatus} disabled={statusLoading}
                                    sx={{ borderColor: '#8B2020', color: '#8B2020', borderRadius: 2, fontWeight: 700,
                                        '&:hover': { bgcolor: 'rgba(139,32,32,0.06)' } }}>
                                {t('manager_profile.inactivate', 'Inactivate')}
                            </Button>
                        )}
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}
                                sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, borderRadius: 2, fontWeight: 700,
                                    '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' } }}>
                            {t('common.edit')}
                        </Button>
                    </Stack>
                </Paper>
                {statusError && <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert>}

                {/* Manager info */}
                <Paper elevation={0} sx={{
                    borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
                    bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                }}>
                    <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                            {t('guide_profile.assignment', 'Assignment')}
                        </Typography>
                    </Box>
                    <Box sx={{ px: 3, py: 1 }}>
                        <InfoRow icon={<LocationCityIcon fontSize="small" />}
                                 label={t('guide_profile.district', 'District')} value={districtLabel} />
                        <InfoRow icon={<CalendarMonthIcon fontSize="small" />}
                                 label={t('guide_profile.start_date', 'Start Date')}
                                 value={manager.startDate
                                     ? new Date(manager.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                     : null} />
                    </Box>
                </Paper>
            </Container>

            {/* ── Edit dialog ─────────────────────────────────────────────────── */}
            <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('guide_profile.edit_title', 'Update Assignment')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }} component="form" onSubmit={submitEdit}>
                        {/* ── FIX: Show name with region fallback in picklist ── */}
                        <TextField
                            label={t('guide_profile.district', 'District')}
                            value={form.districtId}
                            onChange={e => setForm(f => ({ ...f, districtId: e.target.value }))}
                            select fullWidth>
                            <MenuItem value=""><em>{t('guide_profile.no_change', '— No change —')}</em></MenuItem>
                            {districts.map(d => (
                                <MenuItem key={d.id} value={d.id}>
                                    {d.name || d.region || d.id}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('guide_profile.start_date', 'Start Date')}
                            type="date"
                            value={form.startDate}
                            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        {formError && <Alert severity="warning">{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeEdit}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={submitEdit}
                            sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {t('common.next', 'Next')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Password confirmation ───────────────────────────────────────── */}
            <Dialog open={pwdOpen} onClose={closePwd} fullWidth maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('users.confirm_title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('guide_profile.confirm_text', 'Enter your password to confirm the changes.')}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('users.your_password')}
                        type={showPwd ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmSave(); }}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                        {showPwd ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closePwd} disabled={pwdLoading}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={confirmSave} disabled={pwdLoading}
                            sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {pwdLoading ? t('common.saving') : t('users.confirm_btn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}